import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { t } from '../lib/i18n';
import { messageFor } from '../lib/errors';
import { formatNumber } from '../ui/format';
import { Badge, Loading, Panel } from '../ui/Page';
import { getExperiment, setExperiment, type PageVersion, type VariantSplit } from './pages-api';

/**
 * Splitting traffic between two versions of a page, and reading which won.
 *
 * A visitor is kept on the variant they were given by a cookie, so the numbers are people rather
 * than page loads. The split is set as whole percents that must add to 100 — the server refuses
 * anything else, and saying so here spares somebody the round trip.
 *
 * No winner is declared. Two conversions out of three is not a better page than thirty out of
 * sixty, and a screen that puts a crown on the first is a screen that makes people ship the
 * wrong version. It shows the counts and the rate; the judgement stays with the person.
 */
export function ExperimentPanel({
  pageId,
  versions,
}: {
  pageId: string;
  versions: PageVersion[];
}): React.JSX.Element {
  const client = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const [splits, setSplits] = useState<VariantSplit[] | null>(null);

  const report = useQuery({
    queryKey: ['page-experiment', pageId],
    queryFn: () => getExperiment(pageId),
  });

  const save = useMutation({
    mutationFn: (next: VariantSplit[]) => setExperiment(pageId, next),
    onSuccess: () => {
      setError(null);
      setSplits(null);
      void client.invalidateQueries({ queryKey: ['page-experiment', pageId] });
      void client.invalidateQueries({ queryKey: ['page', pageId] });
    },
    onError: (caught: unknown) => setError(messageFor(caught)),
  });

  if (report.isLoading) return <Loading />;

  const results = report.data?.variants ?? [];

  /*
   * Two or more, because one variant at 100% is not an experiment.
   *
   * Publishing writes a single variant row, so "any variants at all" would put a running badge
   * on every published page and invite somebody to read one column of numbers as a comparison.
   */
  const running = results.length > 1;

  /* Two versions are the least an experiment can be, and the newest two are what people mean. */
  const newest = [...versions].sort((a, b) => b.version - a.version).slice(0, 2);

  const draft =
    splits ??
    (running
      ? results.map((result, index) => ({
          variant: result.variant,
          version:
            versions.find((version) => version.variant === result.variant)?.version ??
            newest[index]?.version ??
            1,
          weight: Math.round(100 / results.length),
        }))
      : newest.map((version, index) => ({
          variant: index === 0 ? 'A' : 'B',
          version: version.version,
          weight: 50,
        })));

  const total = draft.reduce((sum, split) => sum + split.weight, 0);
  const winner = best(results);

  return (
    <Panel
      title={t('pages.experiment')}
      actions={running ? <Badge tone="positive">{t('pages.experimentOn')}</Badge> : undefined}
    >
      <p className="muted small">{t('pages.experimentHint')}</p>

      {versions.length < 2 ? (
        <p className="muted">{t('pages.needTwoVersions')}</p>
      ) : (
        <>
          <ul className="conditions__list">
            {draft.map((split, index) => (
              <li key={split.variant} className="conditions__row">
                <Badge tone="neutral">{split.variant}</Badge>

                <select
                  className="select select--compact"
                  value={split.version}
                  aria-label={t('pages.variantVersion')}
                  onChange={(event) =>
                    setSplits(
                      draft.map((entry, at) =>
                        at === index ? { ...entry, version: Number(event.target.value) } : entry,
                      ),
                    )
                  }
                >
                  {versions.map((version) => (
                    <option key={version.id} value={version.version}>
                      {t('automations.versionN').replace('{n}', String(version.version))}
                    </option>
                  ))}
                </select>

                <label className="cell-inline small">
                  <input
                    className="points-input"
                    type="number"
                    min={0}
                    max={100}
                    value={split.weight}
                    aria-label={t('pages.variantWeight')}
                    onChange={(event) =>
                      setSplits(
                        draft.map((entry, at) =>
                          at === index ? { ...entry, weight: Number(event.target.value) } : entry,
                        ),
                      )
                    }
                  />
                  <span className="muted">%</span>
                </label>

                {results[index] && (
                  <span className="muted small">
                    {t('pages.variantResult')
                      .replace('{visits}', formatNumber(results[index]!.visits))
                      .replace('{conversions}', formatNumber(results[index]!.conversions))
                      .replace('{rate}', String(results[index]!.conversionRate))}
                    {winner === results[index]!.variant && ` · ${t('pages.leading')}`}
                  </span>
                )}
              </li>
            ))}
          </ul>

          {total !== 100 && (
            <p className="lead-idle small">
              {t('pages.splitMustBe100').replace('{total}', String(total))}
            </p>
          )}

          {error && (
            <p className="alert small" role="alert">
              {error}
            </p>
          )}

          <button
            type="button"
            className="button button--compact"
            disabled={total !== 100 || save.isPending}
            onClick={() => save.mutate(draft)}
          >
            {running ? t('pages.updateSplit') : t('pages.startExperiment')}
          </button>
        </>
      )}
    </Panel>
  );
}

/**
 * Which variant is ahead, or none.
 *
 * "Ahead" and "wins" are different words on purpose. This is the better rate among variants that
 * have had enough traffic to mean anything; thirty visits is not significance, it is the point
 * below which a rate is noise, and calling anything below it a winner would be a lie in a badge.
 */
export function best(
  results: { variant: string; visits: number; conversionRate: number }[],
): string | null {
  const eligible = results.filter((result) => result.visits >= 30);

  if (eligible.length < 2) return null;

  const sorted = [...eligible].sort((a, b) => b.conversionRate - a.conversionRate);

  return sorted[0]!.conversionRate > sorted[1]!.conversionRate ? sorted[0]!.variant : null;
}
